import styled from 'styled-components';
import { PaymentReturnData } from './Container';

interface PresenterProps {
    paymentData: PaymentReturnData | null;
    isLoading: boolean;
    onGoHome: () => void;
    onRetry: () => void;
}

const Presenter = ({ paymentData, isLoading, onGoHome, onRetry }: PresenterProps) => {
    if (isLoading) {
        return (
            <PageContainer>
                <ContentCard>
                    <LoadingSpinner />
                    <Title>Procesando...</Title>
                </ContentCard>
            </PageContainer>
        );
    }

    if (!paymentData) {
        return (
            <PageContainer>
                <ContentCard>
                    <IconContainer $status="error">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" />
                            <path d="M12 8v4M12 16h.01" />
                        </svg>
                    </IconContainer>
                    <Title>No se encontró información de pago</Title>
                    <Subtitle>
                        No pudimos encontrar los detalles de tu pago. 
                        Por favor, verifica tu email o contacta al negocio.
                    </Subtitle>
                    <ButtonPrimary onClick={onGoHome}>
                        Volver al inicio
                    </ButtonPrimary>
                </ContentCard>
            </PageContainer>
        );
    }

    const { status, bookingId } = paymentData;

    // Success state
    if (status === 'approved') {
        return (
            <PageContainer>
                <ContentCard>
                    <IconContainer $status="success">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                            <polyline points="22 4 12 14.01 9 11.01" />
                        </svg>
                    </IconContainer>
                    <Title>¡Pago realizado con éxito!</Title>
                    <Subtitle>
                        Tu reserva ha sido registrada. Recibirás un recordatorio 
                        por WhatsApp 24 horas antes de tu cita para confirmar tu asistencia.
                    </Subtitle>
                    <BookingInfo>
                        <InfoLabel>ID de Reserva</InfoLabel>
                        <InfoValue>{bookingId}</InfoValue>
                    </BookingInfo>
                    <ButtonPrimary onClick={onGoHome}>
                        Volver al inicio
                    </ButtonPrimary>
                </ContentCard>
            </PageContainer>
        );
    }

    // Pending state
    if (status === 'pending' || status === 'in_process') {
        return (
            <PageContainer>
                <ContentCard>
                    <IconContainer $status="pending">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" />
                            <polyline points="12 6 12 12 16 14" />
                        </svg>
                    </IconContainer>
                    <Title>Pago en proceso</Title>
                    <Subtitle>
                        Tu pago está siendo procesado. Te notificaremos cuando 
                        se confirme. Esto puede tomar unos minutos.
                    </Subtitle>
                    <BookingInfo>
                        <InfoLabel>ID de Reserva</InfoLabel>
                        <InfoValue>{bookingId}</InfoValue>
                    </BookingInfo>
                    <ButtonPrimary onClick={onGoHome}>
                        Volver al inicio
                    </ButtonPrimary>
                </ContentCard>
            </PageContainer>
        );
    }

    // Rejected/Failed state
    return (
        <PageContainer>
            <ContentCard>
                <IconContainer $status="error">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="15" y1="9" x2="9" y2="15" />
                        <line x1="9" y1="9" x2="15" y2="15" />
                    </svg>
                </IconContainer>
                <Title>Pago rechazado</Title>
                <Subtitle>
                    No pudimos procesar tu pago. Por favor, verifica los datos 
                    de tu tarjeta o intenta con otro método de pago.
                </Subtitle>
                <ButtonGroup>
                    <ButtonSecondary onClick={onGoHome}>
                        Volver al inicio
                    </ButtonSecondary>
                    <ButtonPrimary onClick={onRetry}>
                        Intentar de nuevo
                    </ButtonPrimary>
                </ButtonGroup>
            </ContentCard>
        </PageContainer>
    );
};

// Styled Components
const PageContainer = styled.div`
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 20px;
`;

const ContentCard = styled.div`
    background: white;
    border-radius: 16px;
    padding: 48px 40px;
    max-width: 480px;
    width: 100%;
    text-align: center;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
`;

const IconContainer = styled.div<{ $status: 'success' | 'pending' | 'error' }>`
    width: 100px;
    height: 100px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 24px;
    
    ${({ $status }) => {
        switch ($status) {
            case 'success':
                return `
                    background: #e8f5e9;
                    color: #4caf50;
                `;
            case 'pending':
                return `
                    background: #fff3e0;
                    color: #ff9800;
                `;
            case 'error':
                return `
                    background: #ffebee;
                    color: #f44336;
                `;
        }
    }}
`;

const Title = styled.h1`
    font-size: 24px;
    font-weight: 700;
    color: #1a1a2e;
    margin: 0 0 12px;
`;

const Subtitle = styled.p`
    font-size: 16px;
    color: #666;
    line-height: 1.6;
    margin: 0 0 32px;
`;

const BookingInfo = styled.div`
    background: #f8f9fa;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 32px;
`;

const InfoLabel = styled.div`
    font-size: 12px;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
`;

const InfoValue = styled.div`
    font-size: 14px;
    color: #333;
    font-family: monospace;
    word-break: break-all;
`;

const ButtonGroup = styled.div`
    display: flex;
    gap: 12px;
    justify-content: center;
    flex-wrap: wrap;
`;

const ButtonPrimary = styled.button`
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    border-radius: 8px;
    padding: 14px 32px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;
    
    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
    }
    
    &:active {
        transform: translateY(0);
    }
`;

const ButtonSecondary = styled.button`
    background: white;
    color: #667eea;
    border: 2px solid #667eea;
    border-radius: 8px;
    padding: 12px 28px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;
    
    &:hover {
        background: #f0f4ff;
    }
`;

const LoadingSpinner = styled.div`
    width: 48px;
    height: 48px;
    border: 4px solid #e0e0e0;
    border-top-color: #667eea;
    border-radius: 50%;
    margin: 0 auto 24px;
    animation: spin 1s linear infinite;
    
    @keyframes spin {
        to {
            transform: rotate(360deg);
        }
    }
`;

export default Presenter;
